import { SUPPORTED_DATE_TIME_LOCALES, type DateTimeLocale, type HourCycle } from './dateTimePreferences';

export interface DateTimeSettings {
  timezone?: string | null;
  dateTimeLocale?: string | null;
  hourCycle?: HourCycle | null;
}

export function validTimezone(value?: string | null): string | null {
  if (!value) return null;
  try {
    return new Intl.DateTimeFormat('en-AU', { timeZone: value }).resolvedOptions().timeZone;
  } catch {
    return null;
  }
}

export function resolveDateTimeSettings(settings: DateTimeSettings = {}, browserLocale = typeof navigator === 'undefined' ? '' : navigator.languages?.[0] || navigator.language, browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone) {
  const timezone = validTimezone(settings.timezone) || validTimezone(browserTimezone) || 'UTC';
  const regions: Record<string, DateTimeLocale> = {
    'Pacific/Auckland': 'en-NZ', 'Pacific/Chatham': 'en-NZ',
    'Europe/London': 'en-GB', 'Europe/Berlin': 'de-DE', 'Europe/Paris': 'fr-FR',
    'Asia/Tokyo': 'ja-JP', 'Asia/Calcutta': 'en-IN', 'Asia/Kolkata': 'en-IN', 'Asia/Singapore': 'en-SG',
    'America/New_York': 'en-US', 'America/Chicago': 'en-US', 'America/Denver': 'en-US',
    'America/Los_Angeles': 'en-US', 'America/Phoenix': 'en-US', 'America/Detroit': 'en-US',
    'America/Indiana/Indianapolis': 'en-US', 'America/Boise': 'en-US',
    'America/Toronto': 'en-CA', 'America/Vancouver': 'en-CA', 'America/Edmonton': 'en-CA',
    'America/Winnipeg': 'en-CA', 'America/Halifax': 'en-CA', 'America/Regina': 'en-CA',
    'America/St_Johns': 'en-CA',
  };
  const supported = (value?: string | null): value is DateTimeLocale => SUPPORTED_DATE_TIME_LOCALES.includes(value as DateTimeLocale);
  let fallbackLocale = 'en-AU';
  try {
    const canonical = Intl.getCanonicalLocales(browserLocale)[0];
    if (canonical && Intl.DateTimeFormat.supportedLocalesOf([canonical]).length) fallbackLocale = canonical;
  } catch {
    fallbackLocale = 'en-AU';
  }
  const locale = supported(settings.dateTimeLocale) ? settings.dateTimeLocale
    : timezone.startsWith('Australia/') ? 'en-AU'
    : regions[timezone] || fallbackLocale;
  const hourCycle = settings.hourCycle === 'h12' || settings.hourCycle === 'h23' ? settings.hourCycle : null;
  return { timezone, locale, hourCycle };
}

let currentSettings: DateTimeSettings = {};
let revision = 0;
const listeners = new Set<() => void>();

export function setDateTimePreferences(settings: DateTimeSettings | null): void {
  const next = { timezone: settings?.timezone, dateTimeLocale: settings?.dateTimeLocale, hourCycle: settings?.hourCycle };
  if (JSON.stringify(currentSettings) === JSON.stringify(next)) return;
  currentSettings = next;
  revision += 1;
  listeners.forEach(listener => listener());
}

export const subscribeDateTimePreferences = (listener: () => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};
export const getDateTimeRevision = () => revision;
export const getDateTimePreferences = () => currentSettings;

type DateValue = string | number | Date | null | undefined;

export function parseUtcTimestamp(value: DateValue): Date {
  if (value instanceof Date) return value;
  if (value == null || value === '') return new Date(NaN);
  const timestamp = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(value)
    ? `${value}Z` : value;
  return new Date(timestamp);
}

function format(value: DateValue, defaults: Intl.DateTimeFormatOptions, options: Intl.DateTimeFormatOptions, settings: DateTimeSettings): string {
  if (value == null || value === '') return '-';
  const dateOnly = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
  const date = parseUtcTimestamp(dateOnly ? `${value}T00:00:00Z` : value);
  if (!Number.isFinite(date.getTime())) return '-';
  const resolved = resolveDateTimeSettings(settings);
  const merged = { ...defaults, ...options, timeZone: dateOnly ? 'UTC' : options.timeZone || resolved.timezone };
  if (resolved.hourCycle) {
    delete merged.hour12;
    merged.hourCycle = resolved.hourCycle;
  } else {
    delete merged.hour12;
    delete merged.hourCycle;
  }
  if (!validTimezone(merged.timeZone)) merged.timeZone = resolved.timezone;
  return new Intl.DateTimeFormat(resolved.locale, merged).format(date);
}

export function formatDate(value: DateValue, options: Intl.DateTimeFormatOptions = {}, settings = currentSettings): string {
  return format(value, Object.keys(options).some(key => ['year', 'month', 'day', 'weekday', 'dateStyle'].includes(key)) ? {} : { year: 'numeric', month: '2-digit', day: '2-digit' }, options, settings);
}
export function formatDateTime(value: DateValue, options: Intl.DateTimeFormatOptions = {}, settings = currentSettings): string {
  return format(value, Object.keys(options).some(key => ['year', 'month', 'day', 'weekday', 'hour', 'minute', 'second', 'dateStyle', 'timeStyle'].includes(key)) ? {} : { year: 'numeric', month: '2-digit', day: '2-digit', hour: 'numeric', minute: '2-digit' }, options, settings);
}
export function formatTime(value: DateValue, options: Intl.DateTimeFormatOptions = {}, settings = currentSettings): string {
  return format(value, Object.keys(options).some(key => ['hour', 'minute', 'second'].includes(key)) ? {} : { timeStyle: 'short' }, options, settings);
}
export function formatRelative(value: DateValue, now = Date.now(), settings = currentSettings): string {
  if (value == null || value === '') return '-';
  const timestamp = parseUtcTimestamp(value).getTime();
  if (!Number.isFinite(timestamp)) return '-';
  const seconds = (timestamp - now) / 1000;
  const unit: Intl.RelativeTimeFormatUnit = Math.abs(seconds) < 60 ? 'second' : Math.abs(seconds) < 3600 ? 'minute' : Math.abs(seconds) < 86400 ? 'hour' : 'day';
  const divisor = { second: 1, minute: 60, hour: 3600, day: 86400 }[unit];
  return new Intl.RelativeTimeFormat(resolveDateTimeSettings(settings).locale, { numeric: 'auto' }).format(Math.round(seconds / divisor), unit);
}

export function dateInputValue(value: DateValue, settings = currentSettings): string {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  if (value == null || value === '') return '';
  const date = parseUtcTimestamp(value);
  if (!Number.isFinite(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: resolveDateTimeSettings(settings).timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(date);
  const part = (type: string) => parts.find(item => item.type === type)?.value;
  return `${part('year')}-${part('month')}-${part('day')}`;
}

export function formatWallTime(value: string): string {
  if (!/^\d{2}:\d{2}(:\d{2})?$/.test(value)) return value;
  return formatTime(`2000-01-01T${value.length === 5 ? `${value}:00` : value}Z`, { timeZone: 'UTC', hour: 'numeric', minute: '2-digit' });
}